import { Typography, IconButton, Menu, MenuItem, Divider, Box, Collapse } from '@mui/material';
import React, { MouseEvent, useRef, useState } from 'react';
import { styled } from 'styled-components';
import CustomAvatar from '../CustomAvatar/CustomAvatar';
import { useAppDispatch, useAppSelector } from '@src/store/hooks';
import { ArrowBack, LogoutIcon, MenuIcon, OrderCartIcon, SearchIcon, SettingsIcon } from '../Icons';
import { getBaseRoute, generateInitialsHelper, truncateField } from '@src/helpers/Helpers';
import { clearUserSession } from '@src/helpers/ApiHelper';
import { setCurrentUser, setPermissions } from '@src/store/reducers/currentUserReducer';
import { useNavigate, useParams } from 'react-router-dom';
import { setToggleMenu } from '@src/store/reducers/UIReducer';
import { IsMobileWidth } from '../MediaQueryUtils';
import SearchBar from '../SearchBar';
import { setSupplierCatalog } from '@src/store/reducers/catalogReducer';
import { setAllOrders } from '@src/store/reducers/orderReducer';
import { setCart } from '@src/store/reducers/cartReducer';
import { setBranches, setRestaurants } from '@src/store/reducers/branchReducer';
import { setAllUsers } from '@src/store/reducers/teamReducer';
import { Option } from '@src/interfaces/type';
import { ApplicationContext } from '@src/contexts/ApplicationContext';

const Container = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	max-width: 100%;
	height: 64px;
	padding: 20px;
	border-bottom: 1.5px solid #f1f1f1;
	position: sticky;
	top: 0;
	background-color: var(--chat-header-bg);
	width: 100%;
	z-index: 20;
	flex-shrink: 0;
	// @media screen and (max-width: 750px) {
	// 	position: fixed;
	// }
`;

const StyledTitle = styled(Typography)`
	color: #1a1d1f;
	font-family: Poppins;
	font-size: 18px;
	font-weight: 600;
	line-height: 23.4px;
	letter-spacing: 0.2px;
	max-width: 320px;
	word-break: break-word;
	max-height: 50px;
`;
const StyledMenuItem = styled(MenuItem)`
	display: flex;
	align-items: center;
	justify-content: flex-start;
	align-content: center;
	height: 50%;
	color: #5c5c5c;
	letter-spacing: 0.2px;
`;

const StyledName = styled(Box)`
	display: flex;
	justify-content: start;
	align-items: center;
	height: 50%;
	padding: 4px 16px;
	color: var(--lighter-text-color);
	font-size: 14px;
	font-weight: 600;
	line-height: 19.1px;
	letter-spacing: 0.2px;
`;

const CartItemsCount = styled('div')({
	position: 'absolute',
	background: '#FFBE00',
	fontSize: '8.5px',
	width: 'auto',
	minWidth: '14px',
	height: '14px',
	display: 'flex',
	alignItems: 'center',
	color: '#1A1D1F',
	fontWeight: '700',
	fontFamily: 'Nunito Sans',
	justifyContent: 'center',
	borderRadius: '50%',
	top: '6px',
	right: '5px',
	border: '0.5px solid #F7F5F4',
});

type PageHeaderProps = {
	title: string;
	activeIcon?: boolean;
	handleArrowBack?: () => void;
	searchBar?: boolean;
	searchValue?: string;
	handleSearch?: any;
	activeHamburgerIcon?: boolean;
	selectedSearchFilter?: string;
	handleSelectChange?: any;
	filters?: Option[];
	cartData?: any;
	handleOpenCart?: any;
	collapse?: boolean;
	setCollapse?: (data: boolean) => void;
};

const PageHeader: React.FC<PageHeaderProps> = ({
	title,
	activeIcon,
	handleArrowBack,
	searchBar = false,
	searchValue = '',
	handleSearch,
	activeHamburgerIcon = true,
	handleSelectChange,
	selectedSearchFilter,
	filters = [
		{ id: '1', value: 'Product' },
		{ id: '2', value: 'Supplier' },
	],
	cartData,
	handleOpenCart,
	collapse,
	setCollapse,
}) => {
	const { apiService }: any = React.useContext(ApplicationContext);
	const currentUser: any = useAppSelector((state) => state?.currentUser?.value);
	const { id } = useParams();
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const isMobileWidth = IsMobileWidth();

	const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null);

	const logOut = () => {
		clearUserSession(undefined, undefined, navigate, () => apiService.logoutCallV2());
		hideMenu();
	};

	const hideMenu = () => {
		setAnchorEl(null);
	};

	const displayMenu = (event: MouseEvent) => {
		setAnchorEl(event.currentTarget);
	};
	const customBackdropStyle: React.CSSProperties = {
		backgroundColor: 'transparent',
	};

	if (searchBar && isMobileWidth) {
		return (
			<Container
				style={{
					height: collapse ? 'auto' : '70px',
					alignItems: 'flex-start',
					flexDirection: 'column',
					justifyContent: 'flex-start',
					gap: '15px',
				}}
			>
				<div
					style={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: 'center',
						width: '100%',
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						{isMobileWidth && activeHamburgerIcon ? (
							<IconButton
								onClick={() => {
									dispatch(setToggleMenu(true));
								}}
								size="small"
							>
								<MenuIcon />
							</IconButton>
						) : null}
						{activeIcon && (isMobileWidth ? !activeHamburgerIcon : true) ? (
							<div
								style={{
									display: 'flex',
									gap: '10px',
									alignItems: 'center',
									justifyContent: 'center',
									height: 'auto',
								}}
							>
								<IconButton onClick={handleArrowBack}>
									<ArrowBack />
								</IconButton>
								<StyledTitle
									variant="h3"
									style={title?.length > 30 && isMobileWidth ? { overflow: 'auto' } : {}}
								>
									{title}
								</StyledTitle>
							</div>
						) : (
							<StyledTitle variant="h3">{title}</StyledTitle>
						)}
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
						{setCollapse ? (
							<IconButton onClick={() => setCollapse(!collapse)}>
								{/* @ts-ignore */}
								<SearchIcon className="searchBar__searchContainer__searchIcon" />
							</IconButton>
						) : null}
						<CustomAvatar
							src={currentUser?.profile}
							style={{ objectFit: 'contain' }}
							className="cursorPointer"
							generateBgColorBy={currentUser?.username}
							onClick={displayMenu}
						>
							{currentUser ? generateInitialsHelper(currentUser?.firstName) : ''}
						</CustomAvatar>
					</div>
				</div>
				<Collapse in={collapse} style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
					<SearchBar
						style={{
							width: '306px',
							height: '36px',
						}}
						value={searchValue}
						onChange={handleSearch}
						isLoading={false}
						onSelectChange={handleSelectChange}
						selectedSearchFilter={selectedSearchFilter}
						filters={filters}
						autoFocus
					/>
				</Collapse>
				<Menu
					anchorEl={anchorEl}
					anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
					transformOrigin={{ vertical: 'top', horizontal: 'right' }}
					keepMounted
					PaperProps={{
						style: {
							height: 'auto',
							boxShadow: '0px 10px 25px 0px #0000001A',
						},
					}}
					MenuListProps={{
						style: {
							height: '100%',
						},
					}}
					BackdropProps={{
						style: customBackdropStyle,
					}}
					open={Boolean(anchorEl)}
					onClose={hideMenu}
					elevation={3}
				>
					<div style={{ width: '220px', background: 'transparent', height: '100%' }}>
						{/* <StyledName>{currentUser?.companyName}</StyledName> */}
						<StyledName>
							<Typography>{truncateField(currentUser?.firstName, 32)}</Typography>
						</StyledName>
						<Divider />
						<StyledMenuItem
							onClick={() => navigate(`/${getBaseRoute(currentUser?.type)}/${id}/settings/general`)}
							data-test-id="settings-button"
						>
							<SettingsIcon style={{ marginRight: '6px' }} /> Settings
						</StyledMenuItem>
						<StyledMenuItem onClick={logOut} data-test-id="logout-button">
							<LogoutIcon style={{ marginRight: '6px' }} /> Logout
						</StyledMenuItem>
					</div>
				</Menu>
			</Container>
		);
	}

	return (
		<Container>
			{isMobileWidth && activeHamburgerIcon ? (
				<IconButton
					onClick={() => {
						dispatch(setToggleMenu(true));
					}}
					size="small"
				>
					<MenuIcon />
				</IconButton>
			) : null}
			{activeIcon && (isMobileWidth ? !activeHamburgerIcon : true) ? (
				<div
					style={{
						display: 'flex',
						gap: '10px',
						alignItems: 'center',
						justifyContent: 'center',
						height: 'auto',
					}}
				>
					<IconButton onClick={handleArrowBack}>
						<ArrowBack />
					</IconButton>
					<StyledTitle
						variant="h3"
						style={title?.length > 30 && isMobileWidth ? { overflow: 'auto' } : {}}
					>
						{title}
					</StyledTitle>
				</div>
			) : (
				<StyledTitle variant="h3">{title}</StyledTitle>
			)}
			{searchBar ? (
				<div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
					{cartData?.length ? (
						<IconButton
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								marginRight: '16px',
								background: '#F7F5F4',
								width: '36px',
								borderRadius: '50%',
								height: '36px',
							}}
							onClick={handleOpenCart}
						>
							{/* @ts-ignore */}
							<CartItemsCount>{cartData?.length}</CartItemsCount>
							<OrderCartIcon style={{ marginRight: '6px' }} />
						</IconButton>
					) : null}
					{
						<SearchBar
							style={{
								width: filters?.length ? '504px' : '360px',
								height: '36px',
							}}
							value={searchValue}
							onChange={handleSearch}
							isLoading={false}
							onSelectChange={handleSelectChange}
							selectedSearchFilter={selectedSearchFilter}
							filters={filters}
						/>
					}
					<CustomAvatar
						src={currentUser?.profile}
						style={{ objectFit: 'contain' }}
						className="cursorPointer"
						generateBgColorBy={currentUser?.username}
						onClick={displayMenu}
					>
						{currentUser ? generateInitialsHelper(currentUser?.firstName) : ''}
					</CustomAvatar>
				</div>
			) : (
				<div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
					{cartData?.length && !isMobileWidth ? (
						<IconButton
							sx={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								marginRight: '16px',
								background: '#F7F5F4',
								width: '36px',
								borderRadius: '50%',
								height: '36px',
							}}
							onClick={handleOpenCart}
						>
							{/* @ts-ignore */}
							<CartItemsCount>{cartData?.length}</CartItemsCount>
							<OrderCartIcon style={{ marginRight: '6px' }} />
						</IconButton>
					) : null}
					<CustomAvatar
						src={currentUser?.profile}
						className="cursorPointer"
						generateBgColorBy={currentUser?.firstName}
						onClick={displayMenu}
					>
						{currentUser ? generateInitialsHelper(currentUser?.firstName) : ''}
					</CustomAvatar>
				</div>
			)}

			<Menu
				anchorEl={anchorEl}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
				keepMounted
				PaperProps={{
					style: {
						height: 'auto',
						boxShadow: '0px 10px 25px 0px #0000001A',
					},
				}}
				MenuListProps={{
					style: {
						height: '100%',
					},
				}}
				BackdropProps={{
					style: customBackdropStyle,
				}}
				open={Boolean(anchorEl)}
				onClose={hideMenu}
				elevation={3}
			>
				<div style={{ width: '220px', background: 'transparent', height: '100%' }}>
					{/* <StyledName>{currentUser?.companyName}</StyledName> */}
					<StyledName>
						<Typography>{truncateField(currentUser?.firstName, 32)}</Typography>
					</StyledName>
					<Divider />
					<StyledMenuItem
						onClick={() => navigate(`/${getBaseRoute(currentUser?.type)}/${id}/settings/general`)}
						data-test-id="settings-button"
					>
						<SettingsIcon style={{ marginRight: '6px' }} /> Settings
					</StyledMenuItem>
					<StyledMenuItem onClick={logOut} data-test-id="logout-button">
						<LogoutIcon style={{ marginRight: '6px' }} /> Logout
					</StyledMenuItem>
				</div>
			</Menu>
		</Container>
	);
};

export default PageHeader;
